import { HomePageInventoryItem } from '@app/data/clients/inventoryHydrationServiceClient/queries/types';
import { FAN_ART_CAROUSEL_PUBLIC_ENDPOINT } from '@app/pages/api/constants';
import {
  ListsButtonInventoryItem,
  ListsInclusions,
} from '@app/types/shop-bff/types';
import { getDiscountTypeAndAmount } from '@app/utils/getPriceComponent';
import { defineMessages, IntlShape } from '@redbubble/next/intl';
import { getSafeUrl } from '@redbubble/utils/getSafeUrl';
import { CarouselMediaCardProps } from 'components/CarouselSection/types';
import { useCallback, useRef, useState } from 'react';
import { PriceProps } from '../../../../components/Price';

export const messages = defineMessages({
  byArtist: {
    defaultMessage: 'by {artistName}',
  },
});

export const mapItemsToMediaCardProps = (
  listsInclusions: ListsInclusions | null
) => {
  return (
    inputData: HomePageInventoryItem,
    intl: IntlShape
  ): CarouselMediaCardProps | null => {
    if (!inputData) return null;
    const cardTitle = `${inputData.work.title} ${inputData.description}`;
    const artistName = inputData?.work.artistShopDisplayName;

    return {
      productId: inputData?.id,
      link:
        inputData.productPageUrls?.url ||
        inputData.productPageUrls?.fallbackUrl ||
        '',
      imageSrc: inputData.preview?.url,
      title: cardTitle,
      workTitle: inputData.description,
      artistName: artistName ?? undefined,
      matureContent: inputData.work.matureContent,
      caption: intl.formatMessage(messages.byArtist, {
        artistName,
      }),
      loading: false,
      testId: `fan-art-carousel-card-${inputData.id}`,
      addToListButtonProps: {
        inventoryItem: inputData as ListsButtonInventoryItem,
        workId: inputData.work.id,
        listsInclusions,
      },
      stockStatus: inputData.stockStatus,
    };
  };
};

export const mapPriceProps = (
  input: HomePageInventoryItem
): PriceProps | null => {
  if (!input.price || !input.volumeDiscount?.thresholds) return null;

  const priceTypeAndAmount = getDiscountTypeAndAmount(
    input.volumeDiscount.thresholds,
    input.price
  );

  if (!priceTypeAndAmount) {
    return null;
  }

  const { variant, discountedAmount } = priceTypeAndAmount;
  const { discount, currency, amount } = input.price;

  return {
    discount,
    currency,
    amount: variant === 'fromPriceStyle' ? discountedAmount : amount,
    variant,
  };
};

interface FanArtVariables {
  id: string;
  famProperties: string[];
  locale: string;
  country: string;
  currency: string;
}

const getData = async (variables: FanArtVariables) => {
  const url = getSafeUrl(
    FAN_ART_CAROUSEL_PUBLIC_ENDPOINT,
    window.location.href
  );

  if (!url) {
    throw new Error('Invalid URL for fan art products endpoint');
  }

  Object.entries(variables).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  }).then((res) => {
    if (!res.ok) {
      throw new Error('Failed to fetch fan art products');
    }
    return res;
  });
  const data: HomePageInventoryItem[] = await response.json();
  return data;
};

const cache = new Map<string, HomePageInventoryItem[]>();

export const useFanArtProducts = (
  initialPropertyId: string,
  initialItems: HomePageInventoryItem[]
) => {
  const [activeProperty, setActiveProperty] = useState(initialPropertyId);
  const [{ fanArtProducts, loading }, setFanArtProducts] = useState<{
    fanArtProducts: HomePageInventoryItem[] | null;
    loading: boolean;
  }>({
    fanArtProducts: initialItems,
    loading: false,
  });

  const latestFetchId = useRef<string>(initialPropertyId);
  cache.set(latestFetchId.current, initialItems);

  const refresh = useCallback((variables: FanArtVariables) => {
    setActiveProperty(variables.id);
    latestFetchId.current = variables.id;
    const cachedFanArtProducts = cache.get(`${variables.id}`);

    if (cachedFanArtProducts) {
      setFanArtProducts({
        fanArtProducts: cachedFanArtProducts,
        loading: false,
      });
    } else {
      setFanArtProducts({ fanArtProducts: null, loading: true });

      getData(variables)
        .then((res) => {
          // Only update state if this is the latest fetch
          if (latestFetchId.current === variables.id && res) {
            setFanArtProducts({
              fanArtProducts: res,
              loading: false,
            });
            cache.set(`${variables.id}`, res);
          }
        })
        .catch(() => {
          if (latestFetchId.current === variables.id) {
            setFanArtProducts({
              fanArtProducts: null,
              loading: false,
            });
          }
        });
    }
  }, []);

  return {
    fanArtProducts,
    activeProperty,
    loading,
    refresh,
  };
};
